import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import Chip from 'tio-ui/components/chip';
import PageHeader from 'tio-ui/components/page-header';
import ElfTile from 'tio-employee/components/slr/elf-tile';
import ReimaginedContributionHistory from 'tio-employee/components/slr/contribution-history';
import LoanStandingAlertCard from 'tio-employee/components/slr/loan-standing-alert-card';
import LoanStandingUploadDrawer from 'tio-employee/components/slr/loan-standing-upload-drawer';
import { shouldShowSlrLoanStandingAlert } from 'tio-employee/utils/slr-loan-standing-verification';
import {
  elfTileState,
  isLrapEnrolled,
  shouldShowElfTile,
} from 'tio-employee/utils/lrap-enrollment';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type FeaturesService from 'tio-common/services/features';
import type { AuthenticatedSlrDashboardModel } from 'tio-employee/routes/authenticated/slr/dashboard';
import type { SlrLoanStandingAuditState } from 'tio-employee/types/slr-loan-standing-verification';

interface SlrDashboardSignature {
  Args: {
    model: AuthenticatedSlrDashboardModel;
  };
}

export default class SlrDashboardRouteComponent extends Component<SlrDashboardSignature> {
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;
  @service declare features: FeaturesService;

  // Post-upload refresh result, keyed to the model value it superseded. A
  // route refresh (e.g. switching the receiving account re-runs the model
  // hook) replaces the model value, which invalidates this override
  // automatically — a plain tracked copy seeded from args would keep showing
  // the OLD account's alert after the model refreshed underneath it.
  @tracked private refreshedLoanStanding?: {
    supersedes: SlrLoanStandingAuditState | null;
    state: SlrLoanStandingAuditState | null;
  };
  @tracked isLoanStandingUploadDrawerOpen = false;

  get loanStandingVerification() {
    const fromModel = this.args.model.loanStandingVerification;
    const refreshed = this.refreshedLoanStanding;
    return refreshed && refreshed.supersedes === fromModel ? refreshed.state : fromModel;
  }

  get reimaginedModel() {
    return this.args.model.reimagined;
  }

  get lrapEnrollmentStatus() {
    return this.args.model.lrapEnrollmentStatus;
  }

  get showElfTile() {
    return shouldShowElfTile(this.features.isEnabled('lrapEnrollment'), this.lrapEnrollmentStatus);
  }

  get elfTileState() {
    return elfTileState(this.lrapEnrollmentStatus);
  }

  get isLrapEnrolled() {
    return isLrapEnrolled(this.lrapEnrollmentStatus);
  }

  get showLoanStandingAlert() {
    return shouldShowSlrLoanStandingAlert(this.loanStandingVerification);
  }

  openLoanStandingUploadDrawer = () => {
    this.isLoanStandingUploadDrawerOpen = true;
  };

  closeLoanStandingUploadDrawer = () => {
    this.isLoanStandingUploadDrawerOpen = false;
  };

  refreshLoanStandingVerification = dropTask(async () => {
    const supersedes = this.args.model.loanStandingVerification;
    const state = await this.slrLoanStandingVerification.fetchCurrentState();
    this.refreshedLoanStanding = { supersedes, state };
    // The scoped refresh can resolve to null (e.g. the receiving account
    // changed mid-flow); close the upload drawer instead of letting the
    // surrounding {{#if}} unmount it while its open flag is still true.
    if (!state) {
      this.isLoanStandingUploadDrawerOpen = false;
    }
  });

  <template>
    <div class="tio-col-tight">
      <PageHeader @title={{t "dashboard_tiles.student_loan_repayment"}}>
        <:actions>
          {{#if this.isLrapEnrolled}}
            <Chip @intent="success" @size="sm" data-test-lrap-enrolled-chip>
              {{t "lrap.enrollment.tile.enrolled"}}
            </Chip>
          {{/if}}
        </:actions>
      </PageHeader>
      {{#if this.showElfTile}}
        <ElfTile @state={{this.elfTileState}} />
      {{/if}}
      {{#if this.loanStandingVerification}}
        {{#if this.showLoanStandingAlert}}
          <div class="px-4">
            <LoanStandingAlertCard
              @state={{this.loanStandingVerification}}
              @onOpenUpload={{this.openLoanStandingUploadDrawer}}
            />
          </div>
        {{/if}}
      {{/if}}
      <ReimaginedContributionHistory @model={{this.reimaginedModel}} />
    </div>

    {{#if this.loanStandingVerification}}
      <LoanStandingUploadDrawer
        @isOpen={{this.isLoanStandingUploadDrawerOpen}}
        @state={{this.loanStandingVerification}}
        @onClose={{this.closeLoanStandingUploadDrawer}}
        @onRefresh={{this.refreshLoanStandingVerification.perform}}
      />
    {{/if}}
  </template>
}
